import { Callout } from "../components/Callout.js";
import { ScreenshotBox } from "../components/ScreenshotBox.js";

export const meta = {
  title: "書類を送信する",
  updatedAt: "2026-07-23",
};

export function Content() {
  return (
    <>
      <p>
        書類送信は <strong>3 ステップ</strong>のモーダルで完結します。
        HubSpot レコード画面の「クラウドサイン」カードから「<strong>クラウドサインで送信</strong>」を
        クリックすると、モーダルが開きます。
      </p>

      <ScreenshotBox src="/guide/screenshots/send-1.png" caption="送信モーダル全体イメージ" />

      <h2>STEP 1: ファイル選択・順序</h2>

      <h3>クラウドサインのテンプレートを利用する（任意）</h3>
      <p>
        STEP 1 の上部にある「<strong>クラウドサインのテンプレートを利用する</strong>」に
        チェックを入れると、クラウドサインに登録済みのテンプレートから書類を作成できます。
      </p>
      <ul>
        <li>ファイルのアップロードは不要になります</li>
        <li>テンプレートに紐付くファイル・ウィジェット・参加者が新書類に複製されます</li>
        <li>「接続設定」→「デフォルト設定」でテンプレートを設定していれば、モーダルを開いた時点で
          自動選択されます（送信時に変更も可）</li>
      </ul>
      <Callout type="info" title="固定署名者との併用">
        テンプレートに事前定義された署名者はそのまま保持されます。加えて HubSpot 側の
        宛先・共有先（プロパティ / 固定送付宛先）が追加で登録されます。
      </Callout>

      <h3>ファイルアップロードで送信する場合</h3>
      <p>送信する PDF を選びます。取得元は 2 通り:</p>
      <ul>
        <li>
          <strong>HubSpot プロパティから追加</strong>: プロパティ設定で指定した
          ファイルプロパティが「HubSpot プロパティから追加」として一覧表示されます。
          各行の「<strong>+ 追加</strong>」を押して送信対象に加えてください（自動では入りません）
        </li>
        <li>
          <strong>ローカルファイルをアップロード</strong>: PC の PDF を直接アップロード
        </li>
      </ul>
      <ul>
        <li>複数ファイルは順番をドラッグで並べ替え可能</li>
        <li>PDF 以外は受け付けない（即時警告）</li>
      </ul>

      <Callout type="warning" title="ファイル容量の上限">
        1 回の送信で添付できる PDF は <strong>合計 3MB 程度</strong>までです。
        送信時のデータ変換で容量が約 1.3 倍になるため、実際のファイルサイズの合計が
        3MB を超えると送信に失敗します。
        <br />
        大容量の図面付き契約書などを送る場合は、ファイルを分割するか、
        クラウドサインの管理画面から直接アップロードしてください。
      </Callout>

      <h3>基本情報の入力</h3>
      <ul>
        <li>
          <strong>書類タイトル</strong>: クラウドサインに表示される書類名。未入力なら
          最初のファイル名が自動で使われる
        </li>
        <li>
          <strong>契約相手の名称</strong>: 取引の関連プライマリ会社から自動取得
          （手動で上書きも可能）
        </li>
        <li>
          <strong>メモ</strong>: 受信者には表示されない管理用メモ
        </li>
      </ul>

      <h3>送信元クライアント ID の選択</h3>
      <p>
        複数の Client ID を登録している場合、ドロップダウンからどの Client ID で
        送信するかを選択できます。既定の Client ID が自動選択されています。
        クラウドサイン上の送信者名は、選択した Client ID の発行者になります。
      </p>
      <p style={{ fontSize: 13, color: "#6b6b68" }}>
        「HubSpot ユーザー権限」で使用できる Client ID を制限している場合、
        許可された Client ID のみが表示されます。1 つだけの場合は
        ドロップダウン自体が表示されず、自動でその Client ID が使われます。
      </p>

      <h2>STEP 2: 宛先指定</h2>
      <p>
        署名者を順序付きで指定します。1 番目から順に署名依頼メールが送られます。
      </p>
      <ul>
        <li>
          HubSpot プロパティから取得した宛先・固定送付宛先は自動セット済み
        </li>
        <li>関連コンタクトから追加する場合は「関連コンタクトから追加」をクリック</li>
        <li>手動追加も可能（氏名・メール・会社名・敬称）</li>
        <li>順序入れ替え可能（ドラッグまたは矢印ボタン）</li>
      </ul>

      <Callout type="warning" title="氏名は 2 文字以上必須">
        クラウドサイン側の制約で、署名者の氏名は 2 文字以上必要です。
        氏名末尾に「様」などの敬称を自動付与する設定（後述）を有効にしていても、
        氏名そのものが 2 文字未満だと送信エラーになるためご注意ください。
      </Callout>

      <h3>氏名末尾の敬称自動付与</h3>
      <p>
        「<strong>接続設定</strong>」タブ →「クライアントID一覧」→「デフォルト設定」の
        「編集」→「受信者氏名の敬称」で「氏名末尾に敬称を自動付与する」を選ぶと、
        関連コンタクトを宛先に追加した時に氏名末尾に「様」等の敬称が自動で付きます。
        宛先編集モーダルで個別に削除・編集も可能です。
      </p>

      <h3>共有先（CC）</h3>
      <p>
        署名権限はないが書類進捗を共有したい相手を追加できます。
        氏名（任意）+ メールアドレスを入力。
      </p>

      <h3>受信者へのメッセージ</h3>
      <p>署名依頼メールに記載される自由文。任意。</p>

      <h2>STEP 3: 確認・送信</h2>
      <p>
        入力内容を一覧で確認します。
      </p>
      <ScreenshotBox src="/guide/screenshots/send-3.png" caption="確認画面" />

      <h3>送信モード</h3>
      <ul>
        <li>
          <strong>下書きモード（推奨）</strong>: クラウドサインに下書きを作成し、URL を
          HubSpot に格納します。クラウドサイン上で内容確認・署名後に送付してください
        </li>
        <li>
          <strong>直接送信モード</strong>: クラウドサインに書類を作成後、即座に宛先に送信します。
          送信者の署名が不要な書類でのみ利用可能です
        </li>
      </ul>
      <p>
        既定値は「<strong>接続設定</strong>」タブ →「デフォルト設定」の「編集」→
        「送信モード」で変更できます。「書類ごとに選択」を選んでおくと、
        この確認画面で都度切り替えできます。
      </p>

      <h3>書類の転送許可</h3>
      <p>
        受信者が、受け取った書類を別の担当者へ転送できるかを決めます。
        既定値は「<strong>接続設定</strong>」タブ →「デフォルト設定」の「編集」→
        「書類の転送設定」で変更できます。「書類ごとに選択」を選んでおくと、
        この確認画面で都度チェックできます。
      </p>

      <Callout type="info" title="クライアントIDごとに設定が変わる場合">
        送信モード・書類の転送設定・受信者氏名の敬称は、クライアントIDごとに
        上書きできます。送信元クライアントIDを切り替えると、この画面の既定値も
        切り替え先の設定に追従します。
      </Callout>

      <h2>送信後の動作</h2>
      <p>
        「<strong>送信</strong>」ボタンを押すと、書類がクラウドサインに作成されます。
        成功すると以下が自動で行われます:
      </p>
      <ul>
        <li>HubSpot レコードの該当プロパティ（ステータス / 書類 ID / 下書き URL）に書き込み</li>
        <li>送信ログに記録</li>
        <li>月間送信件数が +1（プラン上限の管理対象）</li>
      </ul>

      <Callout type="info" title="この後どうなる？">
        以降は 15 分間隔で、クラウドサインの締結状況を HubSpot に自動同期します。
        詳しくは <a href="/guide/status-sync">ステータス自動連携</a> をご覧ください。
      </Callout>
    </>
  );
}
